import { SITE, KEYWORDS } from "./site";

type SeoInput = {
  title: string;
  description: string;
  path: string;
  image?: string;
  keywords?: string;
  type?: "website" | "article";
};

export function buildMeta({
  title,
  description,
  path,
  image,
  keywords,
  type = "website",
}: SeoInput) {
  const url = `${SITE.url}${path}`;
  // Using the clinic image as default OG image
  const defaultOgImage = "https://podomasso.com.br/assets/clinica-podologia-belem-para-interior.jpg";
  const ogImage = image ?? defaultOgImage;
  return [
    { title },
    { name: "description", content: description },
    { name: "keywords", content: keywords ?? KEYWORDS },
    { name: "author", content: `${SITE.professional.name} — ${SITE.name}` },
    { name: "robots", content: "index, follow, max-image-preview:large" },
    { name: "geo.region", content: "BR-PA" },
    { name: "geo.placename", content: "Belém" },
    { name: "geo.position", content: `${SITE.geo.lat};${SITE.geo.lng}` },
    { name: "ICBM", content: `${SITE.geo.lat}, ${SITE.geo.lng}` },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: type },
    { property: "og:url", content: url },
    { property: "og:image", content: ogImage },
    { property: "og:locale", content: "pt_BR" },
    { property: "og:site_name", content: SITE.name },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: ogImage },
  ];
}

export function buildLinks(path: string) {
  return [
    { rel: "canonical", href: `${SITE.url}${path}` },
  ];
}

export function localBusinessSchema() {
  return {
    type: "application/ld+json" as const,
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": ["MedicalBusiness", "LocalBusiness"],
      "@id": `${SITE.url}/#business`,
      name: SITE.name,
      alternateName: "Podomasso Clínica de Podologia",
      description: SITE.description,
      url: SITE.url,
      telephone: SITE.phone,
      email: SITE.email,
      image: "https://podomasso.com.br/assets/clinica-podologia-belem-para-interior.jpg",
      priceRange: "$$",
      medicalSpecialty: "Podiatry",
      address: {
        "@type": "PostalAddress",
        addressLocality: SITE.address.locality,
        addressRegion: SITE.address.region,
        addressCountry: SITE.address.country,
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: SITE.geo.lat,
        longitude: SITE.geo.lng,
      },
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: SITE.hours.schemaDays,
          opens: SITE.hours.open,
          closes: SITE.hours.close,
        },
      ],
      areaServed: {
        "@type": "City",
        name: "Belém",
        containedInPlace: { "@type": "State", name: "Pará" },
      },
      employee: {
        "@type": "Person",
        name: SITE.professional.name,
        jobTitle: SITE.professional.title,
        identifier: SITE.professional.registry,
      },
      sameAs: [SITE.social.instagram],
    }),
  };
}

export function faqSchema(faqs: { q: string; a: string }[]) {
  return {
    type: "application/ld+json" as const,
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    }),
  };
}

export function serviceSchema(s: { name: string; description: string; slug: string }) {
  return {
    type: "application/ld+json" as const,
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "MedicalProcedure",
      name: s.name,
      description: s.description,
      url: `${SITE.url}/servicos/${s.slug}`,
      provider: { "@type": "MedicalBusiness", name: SITE.name, "@id": `${SITE.url}/#business` },
    }),
  };
}

export function breadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    type: "application/ld+json" as const,
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.name,
        item: `${SITE.url}${it.path}`,
      })),
    }),
  };
}
